import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import DateFilters from "../../components/ui/DateFilters";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";

import {
  TransactionStatus,
  TransactionStatusLabel,
  TransactionType,
  TransactionTypeLabel,
  TranscationPaymentMethods,
} from "../../helpers/enums";
import { Chip, pluralize } from "../../helpers/general";
import { useTransactions } from "../../hooks/useTransactions";
import { general } from "../../locales/general";
import RestrictedRoute from "../auth/RestrictedRoute";
import TransactionsTable from "./TransactionsTable";
import { cardStyle } from "../Places/PlacesTable";
import { controlStyle } from "../../helpers/constants";

const PAGE_SIZE = 10;

const paymentLabel = (method: string) =>
  method === TranscationPaymentMethods.CREDIT_CARD
    ? "Carte bancaire"
    : method === TranscationPaymentMethods.WAFACASH
      ? "Wafacash"
      : "Local";

function FilterSelect({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="d-flex flex-column gap-1">
      <label htmlFor={id} className="text-muted mb-0" style={{ fontSize: 11 }}>
        {label}
      </label>
      <select
        id={id}
        className="form-select px-3"
        style={{ ...controlStyle, width: "auto", minWidth: 150 }}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Tous</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function TransactionsPage() {
  const {
    queries,
    transactions,
    isfetchingTransaction: isLoading,
    addQuery,
    setDates,
  } = useTransactions();

  // Any filter change goes back to page 1
  const setFilter = (key: string, value: string) => {
    addQuery(key as any, value as any);
    addQuery("page", 1);
  };

  const total = transactions?.meta?.totalItems ?? 0;

  return (
    <RestrictedRoute>
      <section>
        {/* Title + count */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="fs-4 fw-bold mb-0">Transactions</h5>
          {transactions && (
            <Chip tone="warning">
              {total} {pluralize("transaction", total)}
            </Chip>
          )}
        </div>

        {/* Filters */}
        <div
          style={{ ...cardStyle, padding: "14px 16px" }}
          className="d-flex flex-wrap align-items-end gap-3 mb-3"
        >
          <div className="d-flex flex-column gap-1">
            <span className="text-muted" style={{ fontSize: 11 }}>
              Date
            </span>
            <DateFilters label="" onChange={setDates} />
          </div>

          <FilterSelect
            id="status"
            label="Statut"
            value={queries.status}
            onChange={(v) => setFilter("status", v)}
            options={Object.values(TransactionStatus).map((s) => ({
              value: s,
              label:
                TransactionStatusLabel[
                  s as keyof typeof TransactionStatusLabel
                ],
            }))}
          />

          <FilterSelect
            id="type"
            label="Type"
            value={queries.type}
            onChange={(v) => setFilter("type", v)}
            options={Object.values(TransactionType).map((t) => ({
              value: t,
              label:
                TransactionTypeLabel[t as keyof typeof TransactionTypeLabel],
            }))}
          />

          <FilterSelect
            id="paymentMethod"
            label="Méthode de paiement"
            value={queries.paymentMethod}
            onChange={(v) => setFilter("paymentMethod", v)}
            options={Object.values(TranscationPaymentMethods).map((m) => ({
              value: m,
              label: paymentLabel(m),
            }))}
          />

          <div
            className="d-flex align-items-center gap-2 px-3 ms-auto flex-grow-1"
            style={{ ...controlStyle, minWidth: 200, maxWidth: 320 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              placeholder="Rechercher"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              defaultValue={queries.query}
              onChange={(e) => setFilter("query", e.target.value.toLowerCase())}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              transactions?.items && transactions.items.length > 0 ? (
                <TransactionsTable transactions={transactions.items} />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={queries.page}
          onPageChange={(page) => addQuery("page", page)}
          siblingCount={1}
          totalCount={transactions?.meta?.totalPages ?? 1}
          pageSize={PAGE_SIZE}
        />
      </section>
    </RestrictedRoute>
  );
}
